import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "../../hooks/useConfirm";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import Highlight from "@tiptap/extension-highlight";
import {
  FileText, Plus, Save, Eye, Trash2, Check, Clock, History, BookOpen,
  Bold, Italic, UnderlineIcon, Strikethrough, AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Minus, Undo2, Redo2, ChevronDown, ArrowLeft, Copy
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import DOMPurify from "dompurify";

const API = process.env.REACT_APP_BACKEND_URL;

const TIPI = [
  { key: "cliente", label: "Contratto Cliente", desc: "Per la registrazione nuovi clienti" },
  { key: "commerciale", label: "Contratto Commerciale", desc: "Per i venditori/agenti" },
];

export default function ContrattiTemplatePanel({ token }) {
  const confirm = useConfirm();
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [tipoAttivo, setTipoAttivo] = useState("cliente");
  const [editing, setEditing] = useState(null);
  const [titolo, setTitolo] = useState("");
  const [saving, setSaving] = useState(false);
  const [showNuovo, setShowNuovo] = useState(false);
  const [showStorico, setShowStorico] = useState(false);
  const [storico, setStorico] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showVarDropdown, setShowVarDropdown] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder: "Scrivi il testo del contratto..." }),
      Highlight.configure({ multicolor: true }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-[300px] px-8 py-6",
      },
    },
  });

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/superadmin/contratti-template?tipo=${tipoAttivo}`, { headers });
      setTemplates(res.data);
    } catch {}
  }, [tipoAttivo]);

  const fetchPlaceholders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/superadmin/contratti-template/placeholders/${tipoAttivo}`, { headers });
      setPlaceholders(res.data);
    } catch {}
  }, [tipoAttivo]);

  useEffect(() => { fetchTemplates(); fetchPlaceholders(); }, [fetchTemplates, fetchPlaceholders]);

  const openEditor = (template) => {
    setEditing(template);
    setTitolo(template.titolo);
    if (editor) {
      // Check if content is HTML or plain text
      const content = template.contenuto || "";
      if (content.includes("<") && content.includes(">")) {
        editor.commands.setContent(content);
      } else {
        // Convert plain text with {{variables}} to HTML
        let html = content
          .replace(/\n\n/g, "</p><p>")
          .replace(/\n/g, "<br/>")
          .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
          .replace(/^## (.+)$/gm, "<h2>$1</h2>")
          .replace(/^# (.+)$/gm, "<h1>$1</h1>");
        if (!html.startsWith("<")) html = `<p>${html}</p>`;
        editor.commands.setContent(html);
      }
    }
  };

  const handleCrea = async () => {
    if (!titolo.trim()) { toast.error("Inserisci un titolo"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/superadmin/contratti-template`, {
        tipo: tipoAttivo, titolo: titolo.trim(), contenuto: ""
      }, { headers });
      toast.success("Template creato");
      setShowNuovo(false);
      openEditor(res.data);
      fetchTemplates();
    } catch { toast.error("Errore creazione"); }
    setSaving(false);
  };

  const handleSalva = async (nuovaVersione = true) => {
    if (!editing || !editor) return;
    setSaving(true);
    try {
      const contenuto = editor.getHTML();
      await axios.put(`${API}/api/superadmin/contratti-template/${editing.id}`, {
        titolo, contenuto, nuova_versione: nuovaVersione
      }, { headers });
      const newVer = nuovaVersione ? (editing.versione || 1) + 1 : editing.versione || 1;
      toast.success(nuovaVersione ? `Nuova versione v${newVer} creata` : "Versione sovrascritta");
      setEditing(prev => ({ ...prev, versione: newVer, titolo, contenuto }));
      fetchTemplates();
    } catch { toast.error("Errore salvataggio"); }
    setSaving(false);
    setShowSaveDialog(false);
  };

  const handlePubblica = async (id) => {
    try {
      await axios.post(`${API}/api/superadmin/contratti-template/${id}/pubblica`, {}, { headers });
      toast.success("Contratto pubblicato!");
      fetchTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la pubblicazione");
    }
  };

  const handleElimina = async (id) => {
    if (!await confirm("Eliminare questo template?")) return;
    try {
      await axios.delete(`${API}/api/superadmin/contratti-template/${id}`, { headers });
      toast.success("Eliminato");
      if (editing?.id === id) { setEditing(null); editor?.commands.setContent(""); }
      fetchTemplates();
    } catch { toast.error("Errore"); }
  };

  const handleStorico = async (id) => {
    try {
      const res = await axios.get(`${API}/api/superadmin/contratti-template/${id}/storico`, { headers });
      setStorico(res.data);
      setShowStorico(true);
    } catch { toast.error("Errore"); }
  };

  const handleDeleteVersione = async (templateId, versione) => {
    if (!await confirm(`Eliminare la versione v${versione}?`)) return;
    try {
      await axios.delete(`${API}/api/superadmin/contratti-template/${templateId}/storico/${versione}`, { headers });
      setStorico(prev => prev.filter(s => s.versione !== versione));
      toast.success("Versione eliminata");
    } catch { toast.error("Errore"); }
  };

  const handleRestoreVersione = (s) => {
    if (editor) {
      const content = s.contenuto || "";
      if (content.includes("<")) {
        editor.commands.setContent(content);
      } else {
        let html = content.replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br/>");
        if (!html.startsWith("<")) html = `<p>${html}</p>`;
        editor.commands.setContent(html);
      }
      setTitolo(s.titolo || titolo);
      toast.success(`Contenuto v${s.versione} ripristinato nell'editor`);
      setShowStorico(false);
    }
  };

  const insertVariable = (key) => {
    if (editor) {
      editor.chain().focus().insertContent(
        `<span data-type="variable" class="variable-chip" style="background:#E0E7FF;color:#4338CA;padding:2px 8px;border-radius:4px;font-weight:600;font-size:0.85em">{${key}}</span>&nbsp;`
      ).run();
    }
    setShowVarDropdown(false);
  };

  // Se stiamo editando, mostra l'editor a pagina piena
  if (editing) {
    return (
      <div className="space-y-0" data-testid="contratti-editor-fullpage">
        {/* Top bar editor */}
        <div className="flex items-center justify-between gap-3 pb-4 border-b mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => { setEditing(null); setPreviewMode(false); }} className="p-2 rounded-xl hover:bg-gray-100" data-testid="back-to-list">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <Input value={titolo} onChange={(e) => setTitolo(e.target.value)}
                className="text-lg font-bold border-0 bg-transparent px-0 h-auto focus-visible:ring-0 shadow-none"
                data-testid="editor-titolo" />
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] text-muted-foreground">v{editing.versione || 1}</span>
                {editing.pubblicato && <span className="text-[10px] font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-full">Attivo</span>}
                <span className="text-[10px] text-muted-foreground">{TIPI.find(t => t.key === tipoAttivo)?.label}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => handleStorico(editing.id)} data-testid="storico-btn">
              <History className="h-3.5 w-3.5 mr-1" /> Storico
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPreviewMode(!previewMode)}>
              <Eye className="h-3.5 w-3.5 mr-1" /> {previewMode ? "Editor" : "Anteprima"}
            </Button>
            {!editing.pubblicato && (
              <Button variant="outline" size="sm" className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                onClick={() => handlePubblica(editing.id)} data-testid="pubblica-btn">
                <Check className="h-3.5 w-3.5 mr-1" /> Pubblica
              </Button>
            )}
            <Button size="sm" onClick={() => setShowSaveDialog(true)} disabled={saving} data-testid="salva-btn">
              <Save className="h-3.5 w-3.5 mr-1" /> Salva
            </Button>
          </div>
        </div>

        {previewMode ? (
          <div className="border rounded-2xl bg-white p-8 max-h-[65vh] overflow-y-auto prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderPreviewHTML(editor?.getHTML() || "", placeholders)) }}
            data-testid="preview-area" />
        ) : (
          <>
            {/* Toolbar formattazione (resta fissa: scrolla solo il box di testo sotto) */}
            <div className="flex items-center gap-1 flex-wrap bg-gray-50 border rounded-t-2xl px-3 py-2">
              <ToolBtn icon={Undo2} onClick={() => editor?.chain().focus().undo().run()} title="Annulla" />
              <ToolBtn icon={Redo2} onClick={() => editor?.chain().focus().redo().run()} title="Ripeti" />
              <Sep />
              <ToolBtn icon={Bold} active={editor?.isActive("bold")} onClick={() => editor?.chain().focus().toggleBold().run()} title="Grassetto" />
              <ToolBtn icon={Italic} active={editor?.isActive("italic")} onClick={() => editor?.chain().focus().toggleItalic().run()} title="Corsivo" />
              <ToolBtn icon={UnderlineIcon} active={editor?.isActive("underline")} onClick={() => editor?.chain().focus().toggleUnderline().run()} title="Sottolineato" />
              <ToolBtn icon={Strikethrough} active={editor?.isActive("strike")} onClick={() => editor?.chain().focus().toggleStrike().run()} title="Barrato" />
              <Sep />
              <button onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
                className={`px-2 py-1 rounded text-xs font-bold ${editor?.isActive("heading", { level: 1 }) ? "bg-indigo-100 text-indigo-700" : "text-gray-500 hover:bg-gray-200"}`}>H1</button>
              <button onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                className={`px-2 py-1 rounded text-xs font-bold ${editor?.isActive("heading", { level: 2 }) ? "bg-indigo-100 text-indigo-700" : "text-gray-500 hover:bg-gray-200"}`}>H2</button>
              <Sep />
              <ToolBtn icon={List} active={editor?.isActive("bulletList")} onClick={() => editor?.chain().focus().toggleBulletList().run()} title="Elenco" />
              <ToolBtn icon={ListOrdered} active={editor?.isActive("orderedList")} onClick={() => editor?.chain().focus().toggleOrderedList().run()} title="Elenco numerato" />
              <Sep />
              <ToolBtn icon={AlignLeft} active={editor?.isActive({ textAlign: "left" })} onClick={() => editor?.chain().focus().setTextAlign("left").run()} title="Sinistra" />
              <ToolBtn icon={AlignCenter} active={editor?.isActive({ textAlign: "center" })} onClick={() => editor?.chain().focus().setTextAlign("center").run()} title="Centro" />
              <ToolBtn icon={AlignRight} active={editor?.isActive({ textAlign: "right" })} onClick={() => editor?.chain().focus().setTextAlign("right").run()} title="Destra" />
              <Sep />
              <ToolBtn icon={Minus} onClick={() => editor?.chain().focus().setHorizontalRule().run()} title="Separatore" />
              <Sep />
              {/* Variable dropdown */}
              <div className="relative">
                <button onClick={() => setShowVarDropdown(!showVarDropdown)}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-semibold hover:bg-indigo-100 border border-indigo-200"
                  data-testid="insert-variable-btn">
                  <span>Variabile</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
                {showVarDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-white border rounded-xl shadow-lg z-50 py-1 min-w-[200px]" data-testid="variable-dropdown">
                    {placeholders.map(p => (
                      <button key={p.key} onClick={() => insertVariable(p.key)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 flex items-center justify-between">
                        <span className="font-medium text-gray-700">{p.label}</span>
                        <span className="text-[10px] text-indigo-500 font-mono">{`{${p.key}}`}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Editor area — SOLO questo box scrolla, toolbar e bottoni restano fermi */}
            <div className="border border-t-0 rounded-b-2xl bg-white max-h-[55vh] overflow-y-auto" data-testid="tiptap-editor"
              onClick={() => showVarDropdown && setShowVarDropdown(false)}>
              <EditorContent editor={editor} />
            </div>
          </>
        )}

        {/* Save dialog: sovrascrivere o nuova versione */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Salva modifiche</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground py-2">Come vuoi salvare le modifiche al contratto?</p>
            <DialogFooter className="flex flex-col gap-2 sm:flex-col">
              <Button onClick={() => handleSalva(false)} disabled={saving} variant="outline" className="w-full justify-start h-auto py-3 px-4" data-testid="save-overwrite">
                <div className="text-left">
                  <p className="font-semibold text-sm">Sovrascrivi versione v{editing.versione || 1}</p>
                  <p className="text-xs text-muted-foreground font-normal">Aggiorna il contenuto senza creare una nuova versione</p>
                </div>
              </Button>
              <Button onClick={() => handleSalva(true)} disabled={saving} className="w-full justify-start h-auto py-3 px-4" data-testid="save-new-version">
                <div className="text-left">
                  <p className="font-semibold text-sm">Crea versione v{(editing.versione || 1) + 1}</p>
                  <p className="text-xs text-white/70 font-normal">Salva come nuova versione (la precedente resta nello storico)</p>
                </div>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Storico dialog */}
        <StoricoDialog open={showStorico} onOpenChange={setShowStorico} storico={storico} templateId={editing.id}
          onDelete={handleDeleteVersione} onRestore={handleRestoreVersione} />
      </div>
    );
  }

  // Vista lista template
  return (
    <div className="space-y-6" data-testid="contratti-template-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-black tracking-tight text-slate-900">Contratti Template</h2>
        <Button size="sm" onClick={() => { setShowNuovo(true); setTitolo(""); }} data-testid="nuovo-template-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Nuovo Template
        </Button>
      </div>

      {/* Tab tipo */}
      <div className="flex gap-3">
        {TIPI.map(t => (
          <button key={t.key} onClick={() => { setTipoAttivo(t.key); setEditing(null); }}
            className={`flex-1 p-4 rounded-2xl border-2 transition-all text-left ${tipoAttivo === t.key ? "border-indigo-500 bg-indigo-50" : "border-gray-200 bg-white hover:border-gray-300"}`}
            data-testid={`tab-${t.key}`}>
            <FileText className={`h-5 w-5 ${tipoAttivo === t.key ? "text-indigo-600" : "text-gray-400"}`} />
            <p className="font-bold text-sm mt-2">{t.label}</p>
            <p className="text-xs text-muted-foreground">{t.desc}</p>
          </button>
        ))}
      </div>

      {/* Lista template */}
      {templates.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-gray-200 rounded-2xl">
          <BookOpen className="h-12 w-12 text-gray-300 mb-3" />
          <p className="text-muted-foreground text-sm">Nessun template creato</p>
          <Button size="sm" className="mt-4" onClick={() => { setShowNuovo(true); setTitolo(""); }}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Crea il primo
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map(t => (
            <Card key={t.id} className="cursor-pointer hover:shadow-lg transition-all hover:border-indigo-300"
              onClick={() => openEditor(t)} data-testid={`template-card-${t.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-indigo-500" />
                      <p className="font-bold text-base">{t.titolo}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Versione {t.versione || 1}</span>
                      {t.pubblicato && (
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full flex items-center gap-0.5">
                          <Check className="h-2.5 w-2.5" /> Pubblicato
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Aggiornato: {t.updated_at ? new Date(t.updated_at).toLocaleString("it-IT") : "-"}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    {!t.pubblicato && (
                      <button onClick={(e) => { e.stopPropagation(); handlePubblica(t.id); }}
                        className="p-2 rounded-lg hover:bg-emerald-50 text-emerald-600" title="Pubblica">
                        <Check className="h-4 w-4" />
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); handleElimina(t.id); }}
                      className="p-2 rounded-lg hover:bg-red-50 text-red-400" title="Elimina">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Nuovo Template */}
      <Dialog open={showNuovo} onOpenChange={setShowNuovo}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Nuovo Template {TIPI.find(t => t.key === tipoAttivo)?.label}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <Input value={titolo} onChange={(e) => setTitolo(e.target.value)} placeholder="Titolo del contratto" data-testid="nuovo-template-titolo" />
            <Button onClick={handleCrea} disabled={saving || !titolo.trim()} className="w-full" data-testid="crea-template-btn">
              {saving ? "Creazione..." : "Crea Template"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Toolbar button helper
function ToolBtn({ icon: Icon, active, onClick, title }) {
  return (
    <button onClick={onClick} title={title}
      className={`p-1.5 rounded-lg transition-colors ${active ? "bg-indigo-100 text-indigo-700" : "text-gray-500 hover:bg-gray-200"}`}>
      <Icon className="h-4 w-4" />
    </button>
  );
}
function Sep() { return <div className="w-px h-5 bg-gray-300 mx-0.5" />; }

// Storico Dialog
function StoricoDialog({ open, onOpenChange, storico, templateId, onDelete, onRestore }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[75vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Storico Versioni</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          {storico.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Nessuna versione precedente</p>}
          {storico.map((s, i) => (
            <Card key={i}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-bold">v{s.versione}</span>
                    <span className="text-xs text-muted-foreground">{s.titolo}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-muted-foreground mr-2">{new Date(s.saved_at).toLocaleString("it-IT")}</span>
                    <button onClick={() => onRestore(s)} className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-600" title="Ripristina">
                      <Copy className="h-3 w-3" />
                    </button>
                    <button onClick={() => onDelete(templateId, s.versione)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400" title="Elimina">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                <details>
                  <summary className="text-xs text-indigo-600 cursor-pointer font-medium">Mostra contenuto</summary>
                  <div className="text-xs mt-2 p-3 bg-gray-50 rounded-lg max-h-48 overflow-y-auto prose prose-sm"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(s.contenuto || "<em>Vuoto</em>") }} />
                </details>
              </CardContent>
            </Card>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Render preview HTML with variable replacement
function renderPreviewHTML(html, placeholders) {
  let result = html;
  const examples = {
    denominazione: "Ristorante Demo S.r.l.",
    piva: "IT12345678901",
    codice_fiscale: "12345678901",
    nome_admin: "Mario Rossi",
    email: "info@ristorantedemo.it",
    telefono: "06 1234567",
    codice_univoco: "M5UXCR1",
    pec: "azienda@pec.it",
    pacchetti: "HACCP & Controlli, Gestione Personale",
    importo: "158.00",
    data: new Date().toLocaleDateString("it-IT"),
    indirizzo: "Via Roma 10, 00100 Roma",
  };
  for (const p of placeholders) {
    examples[p.key] = p.esempio;
  }
  // Replace {key} with highlighted examples
  result = result.replace(/\{(\w+)\}/g, (match, key) => {
    const val = examples[key] || match;
    return `<span style="background:#E0E7FF;color:#4338CA;padding:2px 8px;border-radius:4px;font-weight:600">${val}</span>`;
  });
  return result;
}
